<template>
    <div class="age-verification-settings" :key="localConfigKey">
        <!-- Main Toggle -->
        <div class="form-check form-switch mb-4">
            <input class="form-check-input" type="checkbox" v-model="localConfig.enabled" 
                id="ageVerificationEnabled" @change="emitUpdate">
            <label class="form-check-label" for="ageVerificationEnabled">
                <strong>Enable Age Verification</strong>
            </label>
        </div>
        
        <div v-if="localConfig.enabled" class="ps-3 border-start border-2 border-primary">
            <!-- 1. WHEN: Trigger Conditions -->
            <div class="mb-4">
                <h6 class="text-muted mb-3">
                    <i class="fas fa-cog me-2"></i>When to Require Verification
                </h6>
                <div class="form-check form-switch mb-3">
                    <input class="form-check-input" type="checkbox" v-model="localConfig.triggerByTag"
                        id="triggerByTag" @change="emitUpdate">
                    <label class="form-check-label" for="triggerByTag">
                        Only for items with specific tags
                    </label>
                </div>
                <small class="form-text text-muted">
                    When enabled, verification only applies to items with the tags below. When disabled, all orders require verification.
                </small>
            </div>

            <!-- 2. WHAT: Age Requirements by Tag -->
            <div class="mb-4" v-if="localConfig.triggerByTag">
                <h6 class="text-muted mb-2">
                    <i class="fas fa-tags me-2"></i>Tag Requirements
                </h6>
                <p class="text-muted small mb-3">
                    Define which item tags require age verification and the minimum age required.
                </p>
                <div>
                    <div v-for="(ageReq, index) in localConfig.ageRequirements" :key="ageReq._uiKey" class="age-req-row mb-3" @mouseenter="hoveredRow=index" @mouseleave="hoveredRow=null">
                        <div class="row align-items-center">
                            <div class="col-md-5">
                                <label :for="'tagInput' + index" class="form-label small text-muted mb-1">Item Tag</label>
                                <input :id="'tagInput' + index" type="text" class="form-control" placeholder="e.g., ALCOHOL"
                                    v-model="ageReq.tag"
                                    :ref="'tagInput' + index"
                                    @blur="emitUpdate"
                                    @keydown.enter.prevent="focusAgeInput(index)"
                                    maxlength="50"
                                    autocomplete="off"
                                    :autofocus="index === lastAddedIndex"
                                >
                            </div>
                            <div class="col-md-4">
                                <label :for="'ageInput' + index" class="form-label small text-muted mb-1">Minimum Age</label>
                                <div class="input-group">
                                    <input :id="'ageInput' + index" type="number" class="form-control" placeholder="21"
                                        v-model.number="ageReq.minAge"
                                        :ref="'ageInput' + index"
                                        @blur="emitUpdate"
                                        @keydown.enter.prevent="addAgeRequirementAndFocus()"
                                        min="1" max="99"
                                        autocomplete="off"
                                    >
                                    <span class="input-group-text">years</span>
                                </div>
                            </div>
                            <div class="col-md-3 text-end">
                                <button v-if="localConfig.ageRequirements.length > 1" type="button" class="btn btn-outline-danger btn-sm age-req-remove-btn mt-4"
                                    @click="removeAgeRequirement(index)"
                                    :aria-label="'Remove age requirement for tag ' + (ageReq.tag || '')"
                                >
                                    <i class="fas fa-times me-1"></i>Remove
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <button type="button" class="btn btn-outline-primary w-100 mt-2 mb-3 age-req-add-btn" @click="addAgeRequirementAndFocus()">
                    <i class="fas fa-plus me-1"></i> Add Age Requirement
                </button>
            </div>

            <!-- 3. HOW: Verification Method -->
            <div class="mb-4">
                <h6 class="text-muted mb-3">
                    <i class="fas fa-calendar-alt me-2"></i>Verification Method
                </h6>
                <div class="row">
                    <div class="col-md-6">
                        <label for="dobEntryFormat" class="form-label">Date of Birth Format</label>
                        <select v-model="localConfig.dobEntryFormat" class="form-select" id="dobEntryFormat" @change="emitUpdate">
                            <option value="MMDDYY">MM/DD/YY (US Format)</option>
                            <option value="MMDDYYYY">MM/DD/YYYY (US Format - Full Year)</option>
                            <option value="DDMMYY">DD/MM/YY (International)</option>
                            <option value="DDMMYYYY">DD/MM/YYYY (International - Full Year)</option>
                        </select>
                        <small class="form-text text-muted">How customers enter their birth date</small>
                    </div>
                    <div class="col-md-6">
                        <label for="confirmButtonText" class="form-label">Confirmation Button Text</label>
                        <input type="text" class="form-control" id="confirmButtonText"
                            v-model="localConfig.confirmButtonText" 
                            placeholder="Confirm Age"
                            maxlength="50" @blur="emitUpdate">
                        <small class="form-text text-muted">Text shown on the submit button</small>
                    </div>
                </div>
            </div>

            <!-- 3.5. TEXT: Text & Messages -->
            <div class="mb-4">
                <h6 class="text-muted mb-3">
                    <i class="fas fa-comment-dots me-2"></i>Text & Messages
                </h6>
                <div class="row mb-3">
                    <div class="col-md-6">
                        <label for="pinInstructionText" class="form-label">PIN Step Instructions</label>
                        <input type="text" class="form-control" id="pinInstructionText"
                            v-model="localConfig.text.pinInstruction" 
                            placeholder="Enter Staff PIN"
                            maxlength="100" @blur="emitUpdate">
                    </div>
                    <div class="col-md-6">
                        <label for="dobInstructionText" class="form-label">DOB Step Instructions</label>
                        <input type="text" class="form-control" id="dobInstructionText"
                            v-model="localConfig.text.dobInstruction" 
                            placeholder="Enter Date of Birth"
                            maxlength="100" @blur="emitUpdate">
                    </div>
                </div>
                <div class="row mb-3">
                    <div class="col-md-6">
                        <label for="tooManyAttemptsText" class="form-label">Too Many Attempts Message</label>
                        <input type="text" class="form-control" id="tooManyAttemptsText"
                            v-model="localConfig.text.tooManyAttempts" 
                            placeholder="Too many attempts. Please wait {seconds} seconds."
                            maxlength="150" @blur="emitUpdate">
                        <small class="form-text text-muted">Use {seconds} for wait time</small>
                    </div>
                    <div class="col-md-6">
                        <label for="tooYoungText" class="form-label">Under-Age Message</label>
                        <input type="text" class="form-control" id="tooYoungText"
                            v-model="localConfig.text.tooYoung" 
                            placeholder="Must be 21 or older to continue"
                            maxlength="100" @blur="emitUpdate">
                    </div>
                </div>
            </div>

            <!-- 4. APPEARANCE: Visual Customization -->
            <div class="mb-4">
                <h6 class="text-muted mb-3">
                    <i class="fas fa-palette me-2"></i>Visual Appearance
                </h6>
                <div class="form-check form-switch mb-3">
                    <input class="form-check-input" type="checkbox" v-model="localConfig.showCamera"
                        id="showCamera" @change="onShowCameraChange">
                    <label class="form-check-label" for="showCamera">
                        Show camera feed during verification
                    </label>
                </div>
                <div class="row">
                    <div class="col-md-6">
                        <label for="overlayBackgroundColor" class="form-label">Background Color</label>
                        <div class="d-flex align-items-center mb-2">
                            <input type="color" class="form-control form-control-color me-3" id="overlayBackgroundColor"
                                v-model="localConfig.overlay.backgroundColor" style="width: 60px;" @change="emitUpdate">
                            <input type="text" class="form-control" 
                                v-model="localConfig.overlay.backgroundColor"
                                placeholder="#FF0000" maxlength="7" @input="emitUpdate">
                        </div>
                        <small class="form-text text-muted">Full-screen overlay background color</small>
                    </div>
                    <div class="col-md-6">
                        <label for="overlayCustomText" class="form-label">Display Message</label>
                        <textarea class="form-control mb-2" id="overlayCustomText" rows="2"
                            v-model="localConfig.overlay.customText" 
                            placeholder="You must verify age to continue"
                            maxlength="200" @blur="emitUpdate"></textarea>
                        <small class="form-text text-muted">Message shown to customers</small>
                    </div>
                </div>
            </div>

            <!-- 5. FEEDBACK: Audio Alerts -->
            <div class="mb-4">
                <h6 class="text-muted mb-3">
                    <i class="fas fa-volume-up me-2"></i>Audio Feedback
                </h6>
                <div class="form-check form-switch mb-3">
                    <input class="form-check-input" type="checkbox" v-model="localConfig.audio.enabled"
                        id="audioEnabled" @change="emitUpdate">
                    <label class="form-check-label" for="audioEnabled">
                        Play beeps when verification screen appears
                    </label>
                </div>
                <div v-if="localConfig.audio.enabled" class="row">
                    <div class="col-md-4">
                        <label for="beepCount" class="form-label">Number of Beeps</label>
                        <select v-model.number="localConfig.audio.beepCount" class="form-select" id="beepCount" @change="emitUpdate">
                            <option value="1">1 beep</option>
                            <option value="2">2 beeps</option>
                            <option value="3">3 beeps</option>
                            <option value="4">4 beeps</option>
                            <option value="5">5 beeps</option>
                        </select>
                    </div>
                </div>
            </div>

            <!-- 6. SECURITY: Staff Authorization -->
            <div class="mb-4">
                <h6 class="text-muted mb-3">Staff Authorization</h6>
                <div class="row">
                    <div class="col-md-8">
                        <label for="allowedPins" class="form-label mb-2">
                            Authorized Staff PINs
                            <span class="badge bg-secondary ms-1">{{ localConfig.allowedPins?.length || 0 }}</span>
                        </label>
                        
                        <!-- PIN List Display -->
                        <div v-if="localConfig.allowedPins && localConfig.allowedPins.length > 0" class="pin-list mb-3">
                            <div v-for="(pin, index) in localConfig.allowedPins" :key="index" 
                                class="pin-item">
                                <span class="pin-code">{{ pin }}</span>
                                <button type="button" 
                                    class="pin-remove-btn"
                                    @click="removePin(index)">
                                    Remove
                                </button>
                            </div>
                        </div>
                        
                        <!-- Add PIN Input -->
                        <div class="pin-add-section">
                            <div class="input-group mb-2">
                                <input type="text" 
                                    class="form-control" 
                                    id="newPinInput"
                                    v-model="newPinInput"
                                    placeholder="Enter PIN"
                                    maxlength="20"
                                    @keydown.enter.prevent="addPin"
                                    @input="validatePinInput"
                                    :class="{ 'is-invalid': pinInputError }"
                                    ref="newPinInput">
                                <button type="button" 
                                    class="btn btn-primary"
                                    @click="addPin"
                                    :disabled="!canAddPin">
                                    Add PIN
                                </button>
                            </div>
                            <div v-if="pinInputError" class="text-danger small mb-2">
                                {{ pinInputError }}
                            </div>
                            <small class="form-text text-muted">
                                Only staff with these PINs can authorize age verification.
                            </small>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AgeVerificationSettings',
    props: {
        config: {
            type: Object,
            required: true
        }
    },
    emits: ['update'],
    data() {
        return {
            localConfig: this.initializeConfig(),
            localConfigKey: 0,
            nextAgeReqKey: 1, // for unique row keys
            lastAddedIndex: null,
            hoveredRow: null,
            newPinInput: '',
            pinInputError: null
        };
    },
    computed: {
        canAddPin() {
            return this.newPinInput.trim().length > 0 && !this.pinInputError;
        }
    },
    methods: {
        initializeConfig() {
            const initial = {
                enabled: this.config.enabled !== undefined ? this.config.enabled : false,
                triggerByTag: this.config.triggerByTag !== undefined ? this.config.triggerByTag : true,
                pinRequired: true, // Always required
                showCamera: this.config.showCamera !== undefined ? this.config.showCamera : false,
                dobEntryFormat: this.config.dobEntryFormat || "MMDDYY",
                confirmButtonText: this.config.confirmButtonText || "Confirm Age",
                overlay: {
                    backgroundColor: (this.config.overlay && this.config.overlay.backgroundColor) || "#FF0000",
                    customText: (this.config.overlay && this.config.overlay.customText) || "You must verify age to continue"
                },
                allowedPins: [...(this.config.allowedPins || ["1234", "9999"])],
                ageRequirements: [...(this.config.ageRequirements || [{ tag: 'ALCOHOL', minAge: 21 }])],
                audio: {
                    enabled: this.config.audio && this.config.audio.enabled !== undefined ? this.config.audio.enabled : true,
                    beepCount: this.config.audio && this.config.audio.beepCount !== undefined ? this.config.audio.beepCount : 3
                },
                text: {
                    pinInstruction: this.config.text?.pinInstruction || "Enter Staff PIN",
                    dobInstruction: this.config.text?.dobInstruction || "Enter Date of Birth",
                    invalidPin: this.config.text?.invalidPin || "Enter a valid PIN",
                    incorrectPin: this.config.text?.incorrectPin || "Incorrect PIN",
                    tooManyAttempts: this.config.text?.tooManyAttempts || "Too many attempts. Please wait {seconds} seconds.",
                    enterDob: this.config.text?.enterDob || "Please enter your date of birth",
                    incompleteDate: this.config.text?.incompleteDate || "Please enter complete date ({format})",
                    invalidDate: this.config.text?.invalidDate || "Please enter a valid date",
                    tooYoung: this.config.text?.tooYoung || "Must be 21 or older to continue"
                }
            };
            // Add unique keys for UI
            initial.ageRequirements = initial.ageRequirements.map(req => ({ ...req, _uiKey: Math.random().toString(36).substr(2, 9) }));
            return initial;
        },
        validatePinInput() {
            const pin = this.newPinInput.trim();
            this.pinInputError = null;
            
            if (pin.length === 0) {
                return;
            }
            
            if (pin.length < 2) {
                this.pinInputError = 'PIN must be at least 2 characters';
                return;
            }
            
            if (pin.length > 20) {
                this.pinInputError = 'PIN cannot exceed 20 characters';
                return;
            }
            
            // Check for duplicates
            if (this.localConfig.allowedPins && this.localConfig.allowedPins.includes(pin)) {
                this.pinInputError = 'This PIN already exists';
                return;
            }
        },
        addPin() {
            const pin = this.newPinInput.trim();
            
            if (!pin || this.pinInputError) {
                return;
            }
            
            // Final validation
            if (pin.length < 2) {
                this.pinInputError = 'PIN must be at least 2 characters';
                return;
            }
            
            if (this.localConfig.allowedPins && this.localConfig.allowedPins.includes(pin)) {
                this.pinInputError = 'This PIN already exists';
                return;
            }
            
            // Add PIN
            if (!this.localConfig.allowedPins) {
                this.localConfig.allowedPins = [];
            }
            this.localConfig.allowedPins.push(pin);
            this.newPinInput = '';
            this.pinInputError = null;
            this.emitUpdate();
            
            // Focus back on input
            this.$nextTick(() => {
                if (this.$refs.newPinInput) {
                    this.$refs.newPinInput.focus();
                }
            });
        },
        removePin(index) {
            if (this.localConfig.allowedPins && index >= 0 && index < this.localConfig.allowedPins.length) {
                this.localConfig.allowedPins.splice(index, 1);
                this.emitUpdate();
            }
        },
        addAgeRequirementAndFocus() {
            this.localConfig.ageRequirements.push({ tag: '', minAge: 21, _uiKey: 'ageReq' + (this.nextAgeReqKey++) });
            this.lastAddedIndex = this.localConfig.ageRequirements.length - 1;
            this.$nextTick(() => {
                const idx = this.lastAddedIndex;
                const ref = this.$refs['tagInput' + idx];
                if (ref && ref[0]) ref[0].focus();
            });
        },
        focusAgeInput(index) {
            this.$nextTick(() => {
                const ref = this.$refs['ageInput' + index];
                if (ref && ref[0]) ref[0].focus();
            });
        },
        removeAgeRequirement(index) {
            if (this.localConfig.ageRequirements.length > 1) {
                this.localConfig.ageRequirements.splice(index, 1);
                this.emitUpdate();
            }
        },
        emitUpdate() {
            // Filter out empty age requirements only on save
            const filteredConfig = {
                ...this.localConfig,
                ageRequirements: this.localConfig.ageRequirements.filter(req => 
                    req.tag && req.tag.trim() !== '' && req.minAge && req.minAge > 0
                )
            };
            console.log('Emitting config update:', filteredConfig);
            this.$emit('update', filteredConfig);
        },
        onShowCameraChange() {
            console.log('Show camera changed to:', this.localConfig.showCamera);
            this.emitUpdate();
        }
    },
    watch: {
        config: {
            handler() {
                this.localConfig = this.initializeConfig();
                this.localConfigKey++;
            },
            deep: true,
            immediate: true
        }
    }
};
</script>

<style scoped>
.age-req-row {
    background: #f8f9fa;
    border-radius: 0.5rem;
    padding: 1rem;
    transition: background 0.2s;
    border: 1px solid #e9ecef;
}
.age-req-row:hover, .age-req-row:focus-within {
    background: #e9ecef;
    border-color: #dee2e6;
}
.age-req-remove-btn {
    opacity: 0.7;
    transition: opacity 0.2s;
    white-space: nowrap;
}
.age-req-row:hover .age-req-remove-btn,
.age-req-row:focus-within .age-req-remove-btn {
    opacity: 1;
}
.age-req-add-btn {
    font-weight: 500;
    letter-spacing: 0.01em;
    border-style: dashed;
}
.age-req-add-btn:hover {
    border-style: solid;
}

/* PIN Management Styles */
.pin-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.pin-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.625rem 0.875rem;
    background: #f8f9fa;
    border-radius: 0.375rem;
    border: 1px solid #dee2e6;
}

.pin-code {
    font-size: 0.95rem;
    font-weight: 500;
    color: #212529;
    font-family: 'Courier New', monospace;
}

.pin-remove-btn {
    background: none;
    border: none;
    padding: 0.25rem 0.5rem;
    color: #dc3545;
    font-size: 0.875rem;
    cursor: pointer;
    text-decoration: none;
}

.pin-remove-btn:hover {
    text-decoration: underline;
}

.pin-add-section {
    margin-top: 0.5rem;
}
</style> 